// Staff side of the patient portal and feedback (docs/plans/phase3-portal-feedback-kiswahili.md
// §3.3, §7.2). The server checks every permission; the screens only hide what can't be used.
import {
  feedbackDetailSchema,
  feedbackPageSchema,
  feedbackRequestResultSchema,
  feedbackTrendsSchema,
  patientPortalSchema,
  portalConsentTextSchema,
  portalSettingsSchema,
} from '@wodi/shared';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { api } from './client.ts';

export const portalStaffKeys = {
  patient: (id: string) => ['patients', id, 'portal'] as const,
  consentTexts: ['portal-consent-texts'] as const,
  settings: ['admin', 'portal-settings'] as const,
  adminConsentTexts: ['admin', 'portal-consent-texts'] as const,
  feedback: ['feedback'] as const,
};

export const patientPortalQuery = (patientId: string) =>
  queryOptions({
    queryKey: portalStaffKeys.patient(patientId),
    queryFn: () => api('GET', `/patients/${patientId}/portal`, { schema: patientPortalSchema }),
  });

export const portalConsentTextsQuery = queryOptions({
  queryKey: portalStaffKeys.consentTexts,
  queryFn: () => api('GET', '/portal-consent-texts', { schema: z.array(portalConsentTextSchema) }),
});

export function enrolInPortal(
  patientId: string,
  body: { contactId: string; consentTextId: string },
) {
  return api('POST', `/patients/${patientId}/portal`, {
    body: { ...body, decision: 'granted', method: 'verbal' },
    schema: patientPortalSchema,
  });
}

export function changePortalStatus(
  patientId: string,
  action: 'suspend' | 'resume' | 'close',
  reason: string,
) {
  return api('POST', `/patients/${patientId}/portal/${action}`, {
    body: { reason },
    schema: patientPortalSchema,
  });
}

export function linkPortalChild(patientId: string, relationshipId: string) {
  return api('POST', `/patients/${patientId}/portal/dependants`, {
    body: { relationshipId },
    schema: patientPortalSchema,
  });
}

export function endPortalChild(patientId: string, linkId: string, reason: string) {
  return api('POST', `/patients/${patientId}/portal/dependants/${linkId}/end`, {
    body: { reason },
    schema: patientPortalSchema,
  });
}

export function requestFeedback(visitId: string) {
  return api('POST', `/visits/${visitId}/feedback-request`, {
    schema: feedbackRequestResultSchema,
  });
}

export const portalSettingsQuery = queryOptions({
  queryKey: portalStaffKeys.settings,
  queryFn: () => api('GET', '/admin/portal-settings', { schema: portalSettingsSchema }),
});

export function savePortalSettings(body: { enabled: boolean; portalCode: string }) {
  return api('PUT', '/admin/portal-settings', { body, schema: portalSettingsSchema });
}

export function saveFacilityPaybill(facilityId: string, paybillNumber: string | null) {
  return api('PUT', `/admin/facilities/${facilityId}/paybill`, {
    body: { paybillNumber },
    schema: portalSettingsSchema,
  });
}

export const adminPortalConsentTextsQuery = queryOptions({
  queryKey: portalStaffKeys.adminConsentTexts,
  queryFn: () =>
    api('GET', '/admin/portal-consent-texts', { schema: z.array(portalConsentTextSchema) }),
});

export function addPortalConsentText(body: {
  version: string;
  language: 'en' | 'sw';
  body: string;
}) {
  return api('POST', '/admin/portal-consent-texts', { body, schema: portalConsentTextSchema });
}

export const feedbackListQuery = (status: string, rating: string) =>
  queryOptions({
    queryKey: [...portalStaffKeys.feedback, 'list', status, rating],
    queryFn: () =>
      api('GET', `/feedback?status=${status}&rating=${rating}`, { schema: feedbackPageSchema }),
  });

export const feedbackTrendsQuery = queryOptions({
  queryKey: [...portalStaffKeys.feedback, 'trends'],
  queryFn: () => api('GET', '/feedback/trends', { schema: feedbackTrendsSchema }),
});

export const feedbackDetailQuery = (id: string) =>
  queryOptions({
    queryKey: [...portalStaffKeys.feedback, 'detail', id],
    queryFn: () => api('GET', `/feedback/${id}`, { schema: feedbackDetailSchema }),
  });

export function actOnFeedback(id: string, action: 'reply' | 'resolve' | 'escalate', text: string) {
  const body =
    action === 'reply' ? { reply: text } : action === 'resolve' ? { note: text } : { reason: text };
  return api('POST', `/feedback/${id}/${action}`, { body, schema: feedbackDetailSchema });
}
